/* Estimate document editor — a full-screen "mini Google Docs" that opens
   over the Architect workspace. A white paper page sits on a gray canvas
   and reproduces the generated .docx exactly (Development-Plan schema v2:
   title, optional system subtitle, one CASE SECTION per case each with the
   house 5-col table + a live Subtotal line, the document Total line, a
   Notes list and the Summary Timeline block) — every piece editable inline.
   Save re-validates and re-renders the document through the engine and
   stores it. Approved estimates are immutable; a new architecture review
   creates a fresh editable estimate instead of reopening this version.

   Local state is a working copy of the spec, decorated with a stable
   per-row/-case `_uid` (React keys + reorder safety), an `_apisRaw` string
   so the APIs cell survives mid-word commas, and a `_daysRaw` string so the
   tolerant timeline cell can hold in-progress text (`2`, `0.5`, `1–2`,
   `TBD`, `–`) while the parsed value drives the live subtotals. Totals are
   recomputed live with the same rules as the backend (sum numeric days;
   ranges add min/max; TBD/– excluded; weeks = days/5 rounded to 0.5). The
   server remains authoritative — its returned spec is adopted verbatim
   after a save. English strings, scoped .arch-doc-* styles in
   architect.css. */

import { useAuth } from '../auth/AuthContext';
import { stageAccess } from './stageAccess';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  architectApi,
  estimateApi,
  EstimateSpec,
  EstimateModule,
  EstimateCase,
  EstimateDays,
  EstimateStatus,
  ApprovalStatus,
} from './api';
import { autoGrow, deepCopy } from './utils';
import { toast } from './toast';
import { History, Sparkles, Download, Save } from 'lucide-react';
import './estimate-workspace.css';
import { StageHeader, StageFooter } from './StageChrome';
import { parseEstimateDays as parseDays } from './estimateDays';
import WorkspacePaneToggle from './WorkspacePaneToggle';
import { anchorRevision, useCommentDocument, commentBlockAttributes, CommentBlockButton } from './CommentAnchors';
import EstimateChat, {
  ChatEditResult,
  changedModuleKeys,
  firstChangedKey,
  moduleKey,
} from './EstimateChat';

const TITLE_SUFFIX = '. Development Plan & Timelines';
const DEFAULT_TEAM = '1 Middle Engineer';
const MAX_CASES = 8;
const EstimateReadOnlyContext = React.createContext(false);

/* ── side-by-side architecture pane (DESIGN22 §D2) ────────────
   The reviewer wants the approved poster next to the estimate so the two
   can be compared left/right. The pane is a PLAIN scroll container — no
   pointer/wheel capture at all (the poster editor's pinch-zoom viewer
   swallows two-finger gestures, which is exactly the complaint), so a
   trackpad scrolls the review copy the way every other page scrolls.
   Scale is changed only through the explicit −/+/Fit buttons. */
const ARCH_LS_KEY = 'arch.estimateArchPane';
const ARCH_MIN_W = 280; // px — narrowest useful poster column
const ARCH_DOC_MIN_W = 420; // px the document keeps for itself while split
const ARCH_DEFAULT_W = 460;
const ARCH_SCALE_MIN = 0.05;
const ARCH_SCALE_MAX = 4;
/** Below this the split is pointless — the pane becomes a full-screen sheet. */
const ARCH_MOBILE_MQ = '(max-width: 900px)';

interface ArchPaneState {
  open: boolean;
  width: number;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Last pane state (open + width), best-effort — storage may be blocked. */
function readArchPaneState(): ArchPaneState {
  try {
    const raw = localStorage.getItem(ARCH_LS_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        open: p?.open === true,
        width: clamp(Number(p?.width) || ARCH_DEFAULT_W, ARCH_MIN_W, 1200),
      };
    }
  } catch (_) {
    /* ignore — a missing/blocked store just means defaults */
  }
  return { open: false, width: ARCH_DEFAULT_W };
}

/* Working row: the spec module plus editor-only bookkeeping fields that
   are stripped before the spec is sent to the server. `days` is the parsed
   value (or null when `_daysRaw` can't be parsed — a red-ring invalid). */
interface WorkModule {
  _uid: string;
  _apisRaw: string; // raw text of the APIs cell, parsed to apis[] on save
  _daysRaw: string; // raw text of the timeline cell, parsed to `days`
  name: string;
  description: string;
  days: EstimateDays | null;
  tbd_reason: string;
}

interface WorkCase {
  _uid: string;
  name: string;
  modules: WorkModule[];
}

interface WorkSpec {
  version: number;
  client: string;
  system: string;
  cases: WorkCase[];
  notes: string[];
  summary: { deliverables: string; team: string };
}

let uidSeq = 0;
const nextUid = (): string => 'm' + ++uidSeq;

/* ── number / timeline formatting ───────────────────────── */

/** Compact number: 2 → "2", 0.5 → "0.5" (no trailing .0). */
function numStr(n: number): string {
  return Number.isFinite(n) ? String(n) : '';
}

/** A stored days value → the text shown in the timeline cell. */
function daysToRaw(d: EstimateDays | null | undefined): string {
  if (d === 'TBD') return 'TBD';
  if (d === '–' || (d as any) === '-') return '–';
  if (Array.isArray(d) && d.length === 2) return numStr(d[0]) + '–' + numStr(d[1]);
  if (typeof d === 'number' && Number.isFinite(d)) return numStr(d);
  return '';
}

const isMarker = (d: EstimateDays | null): d is 'TBD' | '–' => d === 'TBD' || d === '–';

function timelineError(work: WorkSpec): string | null {
  for (const c of work.cases) {
    for (let index = 0; index < c.modules.length; index++) {
      const m = c.modules[index];
      const label = m.name.trim() || `Module ${index + 1}`;
      if (m.days == null) return `${label}: enter 0.5–60 days in 0.5-day steps, a range, TBD or –.`;
      if (isMarker(m.days) && !m.tbd_reason.trim()) return `${label}: add a reason for ${m.days === 'TBD' ? 'TBD' : 'not applicable'}.`;
    }
  }
  return null;
}

type SaveResult =
  | { status: 'ok'; spec: EstimateSpec }
  | { status: 'ok-stale'; spec: EstimateSpec }
  | { status: 'noop' }
  | { status: 'fail'; error: string }
  | { status: 'busy'; error: string };

/* ── totals (mirror the backend recompute) ──────────────── */

interface Sub {
  min: number;
  max: number;
  excluded: number; // TBD/– markers
  invalid: number; // unparseable cells (red ring)
}

function caseSub(c: WorkCase): Sub {
  let min = 0;
  let max = 0;
  let excluded = 0;
  let invalid = 0;
  for (const m of c.modules) {
    const d = m.days;
    if (d == null) {
      invalid += 1;
    } else if (isMarker(d)) {
      excluded += 1;
    } else if (Array.isArray(d)) {
      min += d[0];
      max += d[1];
    } else {
      min += d;
      max += d;
    }
  }
  return { min, max, excluded, invalid };
}

/** days/5 rounded to the nearest 0.5, formatted compactly. */
function fmtWeeks(days: number): string {
  const w = Math.round((days / 5) * 2) / 2;
  return Number.isInteger(w) ? String(w) : w.toFixed(1);
}

function daysLabel(min: number, max: number): string {
  return min === max ? numStr(min) + ' days' : numStr(min) + '–' + numStr(max) + ' days';
}

function weeksLabel(min: number, max: number): string {
  if (min === 0 && max === 0) return '0 weeks';
  const lo = fmtWeeks(min);
  const hi = fmtWeeks(max);
  // Collapse when both day-bounds round to the SAME week figure
  // (7.5–8.5 days → "1.5 weeks", not "1.5–1.5 weeks") — mirrors the
  // backend's label rule exactly.
  if (lo === hi) return lo + ' week' + (lo === '1' ? '' : 's');
  return lo + '–' + hi + ' weeks';
}

/** The document Total line: `Total working days: N[–M] days ~ X[–Y] weeks`. */
function totalLabel(min: number, max: number): string {
  return 'Total working days: ' + daysLabel(min, max) + ' ~ ' + weeksLabel(min, max);
}

/* ── spec ⇄ working copy ────────────────────────────────── */

function toWorkModule(m: EstimateModule): WorkModule {
  const raw = daysToRaw(m.days);
  return {
    _uid: nextUid(),
    _apisRaw: (m.apis || []).join(', '),
    _daysRaw: raw,
    name: m.name || '',
    description: m.description || '',
    days: parseDays(raw),
    tbd_reason: m.tbd_reason || '',
  };
}

/** Defensive local v1 → v2 wrap (the backend GET already returns v2, but
    the editor must never choke on a stray v1 spec). Mirrors the backend
    toV2: a single case named `Case 1. {system|Development}`, important[] →
    notes, tbd[] items → notes with a 'TBD: ' prefix. */
function wrapV1(spec: EstimateSpec): WorkSpec {
  const notes: string[] = [];
  for (const s of spec.important || []) if (s && s.trim()) notes.push(s);
  for (const s of spec.tbd || []) if (s && s.trim()) notes.push('TBD: ' + s);
  return {
    version: 2,
    client: spec.client || '',
    system: spec.system || '',
    cases: [
      {
        _uid: nextUid(),
        name: 'Case 1. ' + (spec.system || 'Development'),
        modules: (spec.modules || []).map(toWorkModule),
      },
    ],
    notes,
    summary: { deliverables: '', team: DEFAULT_TEAM },
  };
}

function toWork(spec: EstimateSpec): WorkSpec {
  // Trust the backend's v2 shape; fall back to the local wrap for v1.
  if (spec.version !== 2 && !Array.isArray(spec.cases)) return wrapV1(spec);
  const cases: WorkCase[] = (spec.cases || []).map((c): WorkCase => ({
    _uid: nextUid(),
    name: c.name || '',
    modules: (c.modules || []).map(toWorkModule),
  }));
  // A v2 spec must have at least one case for the editor to render into.
  if (!cases.length) {
    cases.push({ _uid: nextUid(), name: '', modules: [newModule()] });
  }
  return {
    version: 2,
    client: spec.client || '',
    system: spec.system || '',
    cases,
    notes: [...(spec.notes || [])],
    summary: {
      deliverables: spec.summary?.deliverables || '',
      team: spec.summary?.team || DEFAULT_TEAM,
    },
  };
}

function newModule(): WorkModule {
  return {
    _uid: nextUid(),
    _apisRaw: '',
    _daysRaw: '1',
    name: '',
    description: '',
    days: 1,
    tbd_reason: '',
  };
}

function parseApis(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 6);
}

/** Build the clean v2 Estimate spec the server validates. Computed fields
    (subtotal / total / total_label / title) are never sent — the server
    recomputes and overwrites them. Empty notes are dropped; strings are
    trimmed. Invalid timeline cells are guarded against before this runs
    (see `save`), so `days` is always a real value here; a stray null is
    coerced to '–' defensively. */
function toSpec(work: WorkSpec): EstimateSpec {
  const cases = work.cases.map((c): EstimateCase => ({
    name: c.name.trim(),
    modules: c.modules.map((m): EstimateModule => {
      const days: EstimateDays = m.days == null ? '–' : m.days;
      const out: EstimateModule = {
        name: m.name.trim(),
        description: m.description.trim(),
        apis: parseApis(m._apisRaw),
        days,
      };
      if (isMarker(days)) out.tbd_reason = m.tbd_reason.trim();
      return out;
    }),
  }));
  return {
    version: 2,
    client: work.client.trim(),
    system: work.system.trim(),
    cases,
    notes: work.notes.map((s) => s.trim()).filter(Boolean),
    summary: {
      deliverables: work.summary.deliverables.trim(),
      team: work.summary.team.trim() || DEFAULT_TEAM,
    },
  };
}

/* ── inline auto-sizing controls ─────────────────────────── */

/** Textarea that grows with its content (single visual line to N lines). */
function AutoTextarea({
  value,
  onChange,
  placeholder,
  className,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
}) {
  const readOnly = React.useContext(EstimateReadOnlyContext);
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Sync measure keeps typing snappy, but on the editor's FIRST commit
    // the overlay/table may not have its final width yet — a squeezed
    // textarea wraps one-char-per-line and scrollHeight explodes into
    // hundreds of px. Re-measure on the next frame (layout settled).
    autoGrow(el);
    const raf = window.requestAnimationFrame(() => {
      if (ref.current) autoGrow(ref.current);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [value]);
  // Noto Sans can land after mount and shift metrics — re-measure once
  // the font face is ready.
  useEffect(() => {
    const fonts = (document as any).fonts;
    if (!fonts || !fonts.ready) return;
    let alive = true;
    fonts.ready.then(() => {
      if (alive && ref.current) autoGrow(ref.current);
    });
    return () => {
      alive = false;
    };
  }, []);
  return (
    <textarea
      ref={ref}
      rows={1}
      className={'arch-doc-field ' + (className || '')}
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      readOnly={readOnly}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Single-line input that grows to fit its text (title client, APIs cell).
    The mirror ::after in CSS measures the value; a trailing space keeps the
    caret from clipping the last glyph. */
function AutoInput({
  value,
  onChange,
  placeholder,
  className,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  ariaLabel?: string;
}) {
  const readOnly = React.useContext(EstimateReadOnlyContext);
  return (
    <span className="arch-doc-autosize" data-value={(value || placeholder || '') + ' '}>
      <input
        type="text"
        size={1}
        className={'arch-doc-field ' + (className || '')}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
      />
    </span>
  );
}

/* ── component ───────────────────────────────────────────── */

interface Props {
  estimateId: string;
  /** The project this estimate belongs to — used only to fetch the poster
      SVG for the side-by-side architecture pane (DESIGN22 §D2). Optional:
      without it the «Show architecture» toggle is simply not offered, so
      an older caller keeps working unchanged. */
  projectId?: string;
  /** changed = at least one successful save happened (parent refreshes the
      estimates list so the status shows 'Awaiting review'). */
  onClose: (changed: boolean) => void;
  embedded?: boolean;
  active?: boolean;
  revision?: number;
  defaultArchitectureOpen?: boolean;
  reviewPanel?: React.ReactNode;
  approvalStatus?: ApprovalStatus | null;
  approvalUserId?: string | null;
  onChanged?: () => void;
  onDraftStateChange?: (state: { dirty: boolean; busy: boolean }) => void;
}

export default function EstimateDocEditor({
  estimateId, projectId, onClose, embedded = false, active = true, revision = 0,
  defaultArchitectureOpen = false, reviewPanel, approvalStatus, approvalUserId, onChanged, onDraftStateChange,
}: Props) {
  const { user, permissions } = useAuth();
  const rights = stageAccess(user, permissions, 'estimate');
  const canViewArchitecture = stageAccess(user, permissions, 'architecture').view;
  const canEditRef = useRef(rights.edit); canEditRef.current = rights.edit;
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  const [refreshPending, setRefreshPending] = useState(false);
  const [refreshAttempt, setRefreshAttempt] = useState(0);
  const forceRefreshRef = useRef(false);
  const loadedEstimateRef = useRef<string | null>(null);
  const [work, setWork] = useState<WorkSpec | null>(null);
  const [status, setStatus] = useState<EstimateStatus | null>(null);
  const statusRef = useRef<EstimateStatus | null>(null);
  const immutable = status === 'approved';
  const readOnly = immutable || !rights.edit;
  const comments = useCommentDocument('estimate', estimateId, anchorRevision(work));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const savedAnyRef = useRef(false);

  /* ── AI chat integration state ─────────────────────────── */
  const [chatOpen, setChatOpen] = useState(false);
  const [aiLauncherHost, setAiLauncherHost] = useState<HTMLDivElement | null>(null);
  const [aiEditing, setAiEditing] = useState(false); // a prompt edit in flight
  // Module-row keys (case name + module name) to flash after an AI edit,
  // matched by NAME because the _uid remap happens on adoption.
  const [flashKeys, setFlashKeys] = useState<Set<string>>(() => new Set());
  // First changed row to scroll into view (nonce forces re-run on repeats).
  const [scrollTarget, setScrollTarget] = useState<{ key: string; n: number } | null>(null);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  /* ── architecture side-by-side pane (DESIGN22 §D2) ─────── */
  // Lazy initialisers — storage is read once, on mount, not every render.
  const [archPaneOpen, setArchOpen] = useState(() => !!projectId && (defaultArchitectureOpen || readArchPaneState().open));
  const archOpen = archPaneOpen && canViewArchitecture;
  const [archWidth, setArchWidth] = useState(() => readArchPaneState().width);
  const [archSvg, setArchSvg] = useState<string | null>(null);
  const [archLoading, setArchLoading] = useState(false);
  const [archError, setArchError] = useState<string | null>(null);
  const [archScale, setArchScale] = useState(1);
  // A manual +/− choice stays put while the pane changes size. Fit restores
  // the responsive view, which is also the initial mode.
  const archFitModeRef = useRef(true);
  // <900px: no split — the pane opens as a full-screen sheet with Close.
  const [archMobile, setArchMobile] = useState<boolean>(() => {
    try {
      return window.matchMedia(ARCH_MOBILE_MQ).matches;
    } catch (_) {
      return false;
    }
  });
  const archScrollRef = useRef<HTMLDivElement>(null);
  const archStageRef = useRef<HTMLDivElement>(null);
  // Intrinsic poster size in SVG user units — the scale multiplies these.
  const archDimRef = useRef<{ w: number; h: number }>({ w: 1880, h: 4000 });
  const bodyRef = useRef<HTMLDivElement>(null);

  /* Initial load and clean workspace re-entry. Never adopt a response over
     a draft or a save/AI edit that began while that read was in flight. */
  useEffect(() => {
    if (embedded && !active) return undefined;
    const alreadyLoaded = loadedEstimateRef.current === estimateId;
    const force = forceRefreshRef.current;
    forceRefreshRef.current = false;
    if (alreadyLoaded && (savingRef.current || aiEditingRef.current || (dirtyRef.current && !force))) {
      setRefreshPending(true);
      return undefined;
    }
    let cancelled = false;
    const revisionAtRead = editRev.current;
    if (!alreadyLoaded) {
      setLoading(true);
      setLoadError(null);
    }
    estimateApi
      .getEstimate(estimateId)
      .then((r) => {
        if (cancelled) return;
        if (embedded && (editRev.current !== revisionAtRead || savingRef.current || aiEditingRef.current || (dirtyRef.current && !force))) {
          setRefreshPending(true);
          return;
        }
        loadedEstimateRef.current = estimateId;
        statusRef.current = r.estimate.status;
        setStatus(r.estimate.status);
        setWork(toWork(r.estimate.spec));
        setDirty(false);
        setRefreshPending(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        if (!alreadyLoaded) setLoadError(err.message || 'Could not load the document');
        else {
          setRefreshPending(true);
          if (force) toast(err.message || 'Could not reload the document', 'err');
        }
      })
      .finally(() => {
        if (!cancelled && !alreadyLoaded) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [estimateId, embedded, active, revision, refreshAttempt]);

  /* Bumped on every edit. A save snapshots it before the (seconds-long)
     server render; if it moved by the time the request resolves, the user
     kept typing during the save — we must NOT clobber those keystrokes
     with the server-authoritative spec. */
  const editRev = useRef(0);

  /* ── mutate helper: deep-copy, apply, mark dirty ───────── */
  const mutate = useCallback((fn: (s: WorkSpec) => void) => {
    if (!canEditRef.current || statusRef.current === 'approved' || aiEditingRef.current) return;
    const current = workRef.current;
    if (!current) return;
    const next = deepCopy(current);
    fn(next);
    workRef.current = next;
    dirtyRef.current = true;
    setWork(next);
    editRev.current += 1;
    setDirty(true);
    setSavedFlash(false);
  }, []);

  /* live per-case subtotals + document total (mirrors the backend) */
  const totals = useMemo(() => {
    const perCase = (work?.cases || []).map(caseSub);
    let min = 0;
    let max = 0;
    let excluded = 0;
    let invalid = 0;
    for (const s of perCase) {
      min += s.min;
      max += s.max;
      excluded += s.excluded;
      invalid += s.invalid;
    }
    return { perCase, min, max, excluded, invalid };
  }, [work]);

  /* Latest-value refs so the async save / AI-edit paths never read stale
     closures (they await seconds-long server work). */
  const workRef = useRef(work);
  const dirtyRef = useRef(dirty);
  const savingRef = useRef(saving);
  const chatOpenRef = useRef(chatOpen);
  const aiEditingRef = useRef(aiEditing);
  useEffect(() => {
    workRef.current = work;
  }, [work]);
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);
  useEffect(() => {
    savingRef.current = saving;
  }, [saving]);
  useEffect(() => {
    chatOpenRef.current = chatOpen;
  }, [chatOpen]);
  useEffect(() => {
    aiEditingRef.current = aiEditing;
  }, [aiEditing]);

  /* subtle flash on the Total line when the figure changes */
  const [flash, setFlash] = useState(false);
  const prevSigRef = useRef<string | null>(null);
  useEffect(() => {
    const sig = totals.min + '/' + totals.max;
    const prev = prevSigRef.current;
    prevSigRef.current = sig;
    if (prev === null || prev === sig) return undefined;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 650);
    return () => clearTimeout(t);
  }, [totals.min, totals.max]);

  /* the 'Saved' chip fades on its own a couple of seconds after a save */
  useEffect(() => {
    if (!savedFlash) return undefined;
    const t = setTimeout(() => setSavedFlash(false), 2600);
    return () => clearTimeout(t);
  }, [savedFlash]);

  /* changed-row flash after an AI edit fades after ~1.2s */
  useEffect(() => {
    if (!flashKeys.size) return undefined;
    const t = setTimeout(() => setFlashKeys(new Set()), 1200);
    return () => clearTimeout(t);
  }, [flashKeys]);

  /* scroll the first changed row into view (next frame — layout settled) */
  useEffect(() => {
    if (!scrollTarget || !active) return undefined;
    const id = window.requestAnimationFrame(() => {
      const el = rowRefs.current.get(scrollTarget.key);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [scrollTarget, active]);

  /* ── architecture pane: media query, persistence, fetch, scale ──
     Everything here is self-contained; nothing below it can change the
     document itself, so the editor's save/approve paths are untouched. */

  // ≤900px has no room for a split — the pane becomes a full-screen sheet.
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia(ARCH_MOBILE_MQ);
    } catch (_) {
      return undefined;
    }
    const onChange = () => setArchMobile(mq.matches);
    onChange();
    // Safari < 14 only has the deprecated addListener.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    (mq as any).addListener(onChange);
    return () => (mq as any).removeListener(onChange);
  }, []);

  // Remember the last split state (open + width) across sessions.
  useEffect(() => {
    try {
      localStorage.setItem(
        ARCH_LS_KEY,
        JSON.stringify({ open: archOpen, width: archWidth }),
      );
    } catch (_) {
      /* ignore — a blocked store just means no memory */
    }
  }, [archOpen, archWidth]);

  // Cached poster text, read through a ref so the fetch effect can skip a
  // re-fetch without listing `archSvg` in its deps (which would cancel the
  // in-flight request the moment it lands).
  const archSvgRef = useRef<string | null>(archSvg);
  useEffect(() => {
    archSvgRef.current = archSvg;
  }, [archSvg]);
  const [archReload, setArchReload] = useState(0);

  useEffect(() => {
    if (!archOpen || !projectId || (embedded && !active)) return undefined;
    if (!embedded && archSvgRef.current) return undefined; // modal cache
    let cancelled = false;
    setArchLoading(true);
    setArchError(null);
    architectApi
      .getSvgText(projectId)
      .then((text) => {
        if (cancelled) return;
        setArchSvg(text || '');
        setArchError(text ? null : 'This project has no architecture poster yet');
      })
      .catch((err: any) => {
        if (cancelled) return;
        setArchError(err?.message || 'The architecture could not be loaded');
      })
      .finally(() => {
        if (!cancelled) setArchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [archOpen, projectId, archReload, embedded, active, revision]);

  /** Scale the poster so its full width fits the pane (the default view). */
  const archFit = useCallback(() => {
    archFitModeRef.current = true;
    const box = archScrollRef.current;
    const { w } = archDimRef.current;
    if (!box || !w) return;
    const avail = box.clientWidth - 24; // the stage's 12px gutters
    if (avail <= 0) return;
    setArchScale(clamp(avail / w, ARCH_SCALE_MIN, ARCH_SCALE_MAX));
  }, []);

  /* Size the poster by writing width/height ON THE SVG (never a transform),
     so the scroll box's scrollWidth/scrollHeight stay honest. Called both
     right after an injection and whenever the scale changes, so a freshly
     injected poster is never left without a size. */
  const applyArchSize = useCallback((scale: number) => {
    const stage = archStageRef.current;
    const svg = stage ? (stage.querySelector('svg') as SVGSVGElement | null) : null;
    if (!svg) return;
    const { w, h } = archDimRef.current;
    svg.style.width = Math.max(1, Math.round(w * scale)) + 'px';
    svg.style.height = Math.max(1, Math.round(h * scale)) + 'px';
  }, []);
  const archScaleRef = useRef(archScale);
  useEffect(() => {
    archScaleRef.current = archScale;
  }, [archScale]);

  // Inject the poster once per open. Plain innerHTML into a static stage —
  // deliberately NO pointer/wheel handlers, so the surrounding box scrolls
  // with the trackpad instead of pinch-zooming.
  useEffect(() => {
    const stage = archStageRef.current;
    if (!stage) return undefined;
    if (!archSvg) {
      stage.innerHTML = '';
      return undefined;
    }
    stage.innerHTML = archSvg;
    const svg = stage.querySelector('svg') as SVGSVGElement | null;
    if (!svg) return undefined;
    let w = 0;
    let h = 0;
    try {
      if (svg.viewBox && svg.viewBox.baseVal && svg.viewBox.baseVal.width) {
        w = svg.viewBox.baseVal.width;
        h = svg.viewBox.baseVal.height;
      }
    } catch (_) {
      /* ignore */
    }
    if (!w) w = parseFloat(svg.getAttribute('width') || '') || 1880;
    if (!h) h = parseFloat(svg.getAttribute('height') || '') || 4000;
    archDimRef.current = { w, h };
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.style.display = 'block';
    svg.style.maxWidth = 'none';
    applyArchSize(archScaleRef.current); // never leave it unsized
    // Fit on the next frame — the pane's width is only real after layout.
    const raf = window.requestAnimationFrame(() => {
      if (archFitModeRef.current) archFit();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [archSvg, archOpen, archMobile, archFit, applyArchSize]);

  // Re-size on every scale change (−/+/Fit).
  useEffect(() => {
    applyArchSize(archScale);
  }, [archScale, applyArchSize]);

  const archZoom = useCallback((factor: number) => {
    archFitModeRef.current = false;
    setArchScale((s) => clamp(s * factor, ARCH_SCALE_MIN, ARCH_SCALE_MAX));
  }, []);

  // The split handle changes this scroll box's width without resizing the
  // window. Follow its *actual* width, including CSS caps and chat changes.
  // ResizeObserver also avoids relying on React's archWidth state to guess
  // how much space the poster finally received after layout.
  useEffect(() => {
    if (!archOpen || !archSvg || (embedded && !active)) return undefined;
    const box = archScrollRef.current;
    if (!box) return undefined;
    let frame: number | null = null;
    let lastWidth = 0;
    const measure = () => {
      frame = null;
      const width = box.clientWidth;
      if (width <= 0 || width === lastWidth) return;
      lastWidth = width;
      if (archFitModeRef.current) archFit();
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(box);
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [archOpen, archSvg, embedded, active, archFit]);

  /* ── splitter drag (desktop split only) ────────────────── */
  const [archDragging, setArchDragging] = useState(false);
  const onSplitDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const body = bodyRef.current;
    if (!body || e.button !== 0) return;
    e.preventDefault();
    const rect = body.getBoundingClientRect();
    const el = e.currentTarget;
    const maxW = Math.max(ARCH_MIN_W, rect.width - ARCH_DOC_MIN_W);
    const move = (ev: PointerEvent) => {
      setArchWidth(clamp(ev.clientX - rect.left, ARCH_MIN_W, maxW));
    };
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      setArchDragging(false);
    };
    try {
      el.setPointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    setArchDragging(true);
  }, []);

  const onSplitKey = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const rect = bodyRef.current?.getBoundingClientRect();
    const maxW = rect ? Math.max(ARCH_MIN_W, rect.width - ARCH_DOC_MIN_W) : 1200;
    const step = e.key === 'ArrowLeft' ? -32 : 32;
    setArchWidth((w) => clamp(w + step, ARCH_MIN_W, maxW));
  }, []);

  /* Esc closes the topmost layer first: the mobile poster sheet, then the
     chat, then the editor. Kept in a ref for the global key handler. */
  const archSheetOpenRef = useRef(false);
  useEffect(() => {
    archSheetOpenRef.current = archOpen && archMobile && !embedded;
  }, [archOpen, archMobile, embedded]);

  const docName = (work?.client.trim() || 'Untitled') + TITLE_SUFFIX;

  /* ── close guard ───────────────────────────────────────── */
  const requestClose = useCallback(() => {
    if (aiEditing) {
      toast('The AI is editing the document — wait for it to finish', 'err');
      return;
    }
    if (saving) return;
    if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose(savedAnyRef.current);
  }, [saving, dirty, aiEditing, onClose]);

  /* ── save core ─────────────────────────────────────────────
     Persists the working copy and (when no edits landed during the
     render) adopts the server-authoritative spec. Returns an outcome the
     Save button and the AI-edit auto-save both branch on. Uses the
     latest-value refs so it is safe to call from any async path. */
  const doSave = useCallback(
    async (forAi = false): Promise<SaveResult> => {
      if (!canEditRef.current || statusRef.current === 'approved') {
        return { status: 'fail', error: 'This estimate is read only.' };
      }
      const w = workRef.current;
      if (!w) return { status: 'fail', error: 'The document is still loading — try again in a moment.' };
      if (savingRef.current || (aiEditingRef.current && !forAi)) {
        return { status: 'busy', error: 'Wait for the current save or edit to finish, then try again.' };
      }
      if (!dirtyRef.current) return { status: 'noop' };
      const invalid = timelineError(w);
      if (invalid) {
        toast(invalid, 'err');
        return { status: 'fail', error: invalid };
      }
      const clean = toSpec(w);
      const revAtSend = editRev.current;
      savingRef.current = true;
      setSaving(true);
      try {
        const r = await estimateApi.saveEstimateSpec(estimateId, clean);
        statusRef.current = r.estimate.status;
        setStatus(r.estimate.status);
        savedAnyRef.current = true;
        onChangedRef.current?.();
        if (editRev.current === revAtSend) {
          // No edits landed during the render — safe to adopt the
          // server-authoritative spec (canonical totals/pruning) and clear.
          const next = toWork(r.estimate.spec);
          workRef.current = next;
          dirtyRef.current = false;
          setWork(next);
          setDirty(false);
          setSavedFlash(true);
          return { status: 'ok', spec: r.estimate.spec };
        }
        // The user kept typing while saving. Keep their live edits and
        // stay dirty so nothing is silently lost; the next save persists.
        return { status: 'ok-stale', spec: r.estimate.spec };
      } catch (err: any) {
        if (err.code === 'approved_document_immutable') {
          statusRef.current = 'approved';
          setStatus('approved');
          setRefreshPending(true);
          const error = 'This estimate was approved elsewhere. It is now read only; reload the saved version.';
          toast(error, 'err');
          return { status: 'fail', error };
        }
        const error = err?.message || 'The document could not be saved. Try again.';
        toast(error, 'err');
        return { status: 'fail', error };
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [estimateId],
  );

  /* ── save (top-bar button / Cmd-S) ─────────────────────── */
  const save = useCallback(async () => {
    const res = await doSave();
    if (res.status === 'ok') toast('Saved — document re-rendered', 'ok');
    else if (res.status === 'ok-stale') toast('Saved — you have newer edits, save again to keep them', 'ok');
  }, [doSave]);

  /* ── AI edit (chat send flow — the correctness core) ─────
     1. auto-save pending manual edits (abort on failure);
     2. snapshot the clean "before" spec;
     3. read-only guard on the page while the request is in flight;
     4. adopt the server spec, flash the changed rows, scroll to the first;
     5. hand before/after/telemetry back to the chat for its diff bubble. */
  const aiEdit = useCallback(
    async (
      instruction: string,
      model: string,
      hooks: { onSavedManual: () => void },
    ): Promise<ChatEditResult> => {
      if (!canEditRef.current || statusRef.current === 'approved') return { ok: false, error: 'This approved estimate is read only.' };
      if (savingRef.current || aiEditingRef.current) return { ok: false, error: 'Wait for the current save or edit to finish, then try again.' };
      const w0 = workRef.current;
      if (!w0) return { ok: false, error: 'The document is still loading — try again in a moment' };
      // Lock the complete save → AI sequence, including keyboard mutations.
      // A prompt must never run against a partially saved working copy.
      aiEditingRef.current = true;
      setAiEditing(true);
      try {
        let before = toSpec(w0);
        if (dirtyRef.current) {
          const res = await doSave(true);
          if (res.status === 'fail' || res.status === 'busy') return { ok: false, error: res.error };
          if (res.status === 'noop' || res.status === 'ok-stale') {
            return { ok: false, error: 'Your newer manual edits are preserved. Save them before asking for this change.' };
          }
          before = res.spec;
          hooks.onSavedManual();
        }
        if (!canEditRef.current || (statusRef.current as EstimateStatus | null) === 'approved') return { ok: false, error: 'This estimate is now read only. Reload its saved version to continue.' };
        const revisionAtPrompt = editRev.current;
        const r = await estimateApi.promptEstimate(estimateId, { instruction, model });
        statusRef.current = r.estimate.status;
        setStatus(r.estimate.status);
        const after = r.estimate.spec;
        savedAnyRef.current = true;
        onChangedRef.current?.();
        if (editRev.current !== revisionAtPrompt) {
          setRefreshPending(true);
          return { ok: false, error: 'Your newer manual edits are preserved. Review them before loading the AI update.' };
        }
        // Adopt exactly like a save: replace the working copy, clear dirty,
        // flash the Saved chip. Editing was blocked while in flight, so no
        // keystrokes can be lost here.
        const next = toWork(after);
        workRef.current = next;
        dirtyRef.current = false;
        setWork(next);
        setDirty(false);
        setSavedFlash(true);
        editRev.current += 1;
        // Flash + scroll: match by NAME (the _uid remap already happened).
        const keys = changedModuleKeys(before, after);
        setFlashKeys(keys);
        const first = firstChangedKey(after, keys);
        if (first) setScrollTarget((p) => ({ key: first, n: (p?.n || 0) + 1 }));
        return { ok: true, before, after, telemetry: r.telemetry ?? null };
      } catch (err: any) {
        if (err.code === 'approved_document_immutable') {
          statusRef.current = 'approved';
          setStatus('approved');
          setRefreshPending(true);
          return { ok: false, error: 'This estimate was approved elsewhere. Reload its saved version to continue.' };
        }
        return { ok: false, error: err.message || 'The edit could not be completed — try again' };
      } finally {
        aiEditingRef.current = false;
        setAiEditing(false);
      }
    },
    [doSave, estimateId],
  );

  /* ── keyboard: Cmd/Ctrl+S saves, Cmd/Ctrl+J toggles chat,
        Esc closes the chat first (then the editor) ─────────── */
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        void save();
      } else if ((e.metaKey || e.ctrlKey) && (e.key === 'j' || e.key === 'J')) {
        e.preventDefault();
        if (canEditRef.current) setChatOpen((o) => !o);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // Close the topmost layer first: the full-screen poster sheet,
        // then the chat, then the editor itself.
        if (archSheetOpenRef.current) setArchOpen(false);
        else if (chatOpenRef.current) setChatOpen(false);
        else if (!embedded) requestClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save, requestClose, active, embedded]);

  useEffect(() => {
    onDraftStateChange?.({ dirty, busy: saving || aiEditing });
  }, [dirty, saving, aiEditing, onDraftStateChange]);

  useEffect(() => () => onDraftStateChange?.({ dirty: false, busy: false }), [onDraftStateChange]);

  // A hidden workspace may receive an AI response, but it must not take focus.
  // Fit again when the pane becomes visible after being mounted in a hidden tab.
  useEffect(() => {
    if (!active || !archOpen) return undefined;
    const raf = window.requestAnimationFrame(() => {
      if (archFitModeRef.current) archFit();
      bodyRef.current?.querySelectorAll<HTMLTextAreaElement>('textarea.arch-doc-field').forEach(autoGrow);
    });
    return () => window.cancelAnimationFrame(raf);
  }, [active, archOpen, archFit]);

  // The workspace column can resize without a window resize (splitter,
  // chat, or a mobile card layout). Re-measure only when its width changes;
  // growing a textarea's height must not trigger a ResizeObserver loop.
  useEffect(() => {
    if (!embedded || !active) return undefined;
    const canvas = bodyRef.current?.querySelector<HTMLElement>('.arch-doc-canvas');
    if (!canvas) return undefined;
    let lastWidth = -1;
    let frame: number | null = null;
    const measure = () => {
      frame = null;
      const width = canvas.clientWidth;
      if (width <= 0 || width === lastWidth) return;
      lastWidth = width;
      canvas.querySelectorAll<HTMLTextAreaElement>('textarea.arch-doc-field').forEach(autoGrow);
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(canvas);
    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [embedded, active]);

  /* ── native tab-close guard while dirty ────────────────── */
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  /* ── download the stored .docx (last saved) ────────────── */
  const download = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await estimateApi.estimateDocxBlob(estimateId);
      const blob = await res.blob();
      let filename = docName + '.docx';
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      if (m) filename = decodeURIComponent(m[1]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast('Document downloaded', 'ok');
    } catch (err: any) {
      toast(err.message, 'err');
    } finally {
      setDownloading(false);
    }
  }, [estimateId, docName]);

  /* ── module operations (scoped to a case) ──────────────── */
  const modIn = (s: WorkSpec, cuid: string): WorkCase | undefined =>
    s.cases.find((c) => c._uid === cuid);

  const setModule = (cuid: string, uid: string, patch: Partial<WorkModule>) =>
    mutate((s) => {
      const m = modIn(s, cuid)?.modules.find((x) => x._uid === uid);
      if (m) Object.assign(m, patch);
    });

  const setDays = (cuid: string, uid: string, raw: string) =>
    mutate((s) => {
      const m = modIn(s, cuid)?.modules.find((x) => x._uid === uid);
      if (!m) return;
      m._daysRaw = raw;
      m.days = parseDays(raw);
    });

  const moveModule = (cuid: string, uid: string, dir: -1 | 1) =>
    mutate((s) => {
      const c = modIn(s, cuid);
      if (!c) return;
      const i = c.modules.findIndex((x) => x._uid === uid);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= c.modules.length) return;
      const t = c.modules[i];
      c.modules[i] = c.modules[j];
      c.modules[j] = t;
    });

  const deleteModule = (cuid: string, uid: string) => {
    if (!window.confirm('Delete this module row?')) return;
    mutate((s) => {
      const c = modIn(s, cuid);
      if (c) c.modules = c.modules.filter((x) => x._uid !== uid);
    });
  };

  const addModule = (cuid: string) =>
    mutate((s) => {
      modIn(s, cuid)?.modules.push(newModule());
    });

  /* ── case operations ───────────────────────────────────── */
  const setCaseName = (cuid: string, v: string) =>
    mutate((s) => {
      const c = modIn(s, cuid);
      if (c) c.name = v;
    });

  const moveCase = (cuid: string, dir: -1 | 1) =>
    mutate((s) => {
      const i = s.cases.findIndex((x) => x._uid === cuid);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= s.cases.length) return;
      const t = s.cases[i];
      s.cases[i] = s.cases[j];
      s.cases[j] = t;
    });

  const deleteCase = (cuid: string) => {
    if (!window.confirm('Delete this case section and all its modules?')) return;
    mutate((s) => {
      if (s.cases.length <= 1) return; // always keep at least one case
      s.cases = s.cases.filter((x) => x._uid !== cuid);
    });
  };

  const addCase = () =>
    mutate((s) => {
      if (s.cases.length >= MAX_CASES) return;
      s.cases.push({ _uid: nextUid(), name: '', modules: [newModule()] });
    });

  /* ── notes list ops ────────────────────────────────────── */
  const addNote = () => mutate((s) => s.notes.push(''));
  const setNote = (i: number, v: string) =>
    mutate((s) => {
      s.notes[i] = v;
    });
  const delNote = (i: number) =>
    mutate((s) => {
      s.notes.splice(i, 1);
    });

  /* ── summary ops ───────────────────────────────────────── */
  const setDeliverables = (v: string) =>
    mutate((s) => {
      s.summary.deliverables = v;
    });
  const setTeam = (v: string) =>
    mutate((s) => {
      s.summary.team = v;
    });

  /* ── chip label ────────────────────────────────────────── */
  let chip: { label: string; cls: string } | null = null;
  if (dirty) chip = { label: 'Unsaved changes', cls: 'dirty' };
  else if (savedFlash) chip = { label: 'Saved', cls: 'saved' };

  // Mobile workspace fields wrap within the available column. The modal
  // keeps its original paper controls; both use the same controlled values.
  const IdentityField = embedded && archMobile ? AutoTextarea : AutoInput;
  const ApiField = embedded ? AutoTextarea : AutoInput;
  const summaryTimeline = weeksLabel(totals.min, totals.max) + ' = ' + (work?.summary.team.trim() || DEFAULT_TEAM);

  const documentControls = (
    <>
      {/* top bar */}
      {embedded ? <StageHeader title="Estimate" status={immutable ? 'Approved' : chip?.label || 'In review'} actions={[
        { id: 'versions', label: 'Version history', icon: <History size={17}/>, disabled: saving, onClick: requestClose },
        ...(projectId && canViewArchitecture ? [{ id: 'reference', label: archOpen ? 'Hide architecture' : 'Show architecture', onClick: () => setArchOpen(value => !value) }] : []),
        { id: 'download', label: 'Download DOCX', icon: <Download size={17}/>, disabled: downloading || loading || aiEditing, onClick: () => { void download(); } },
      ]} decision={<StageFooter placement="header" stage="estimate" status={approvalStatus} userId={approvalUserId} pending={!immutable} title={immutable ? 'Estimate approved' : dirty ? 'Changes ready to save' : reviewPanel ? 'Ready for review' : 'Draft in progress'} hint={dirty ? 'Save before approving' : undefined}>
          {!immutable ? <>
            <button hidden={!rights.edit} className="arch-btn arch-btn-outline" onClick={save} disabled={!dirty || saving || loading || aiEditing}><Save size={15}/>{saving ? 'Saving…' : 'Save draft'}</button>
            {reviewPanel ? <fieldset disabled={dirty || saving || aiEditing}>{reviewPanel}</fieldset> : null}
          </> : null}
        </StageFooter>}/> : (
      <div className={'arch-doc-topbar' + (embedded ? ' arch-workspace-doc-toolbar arch-workspace-pane-head' : '')}>
        <button
          className="arch-btn arch-btn-ghost arch-btn-sm arch-doc-back"
          onClick={requestClose}
          disabled={saving}
          aria-label={embedded ? 'Estimate history' : undefined}
          title={embedded ? 'Estimate history' : undefined}
        >
          {embedded ? <><History size={16} aria-hidden="true" /><span>Estimate history</span></> : '← Estimates'}
        </button>
        <div className="arch-doc-docname" title={docName}>
          {docName}
        </div>
        <div className="arch-doc-topbar-right">
          {immutable ? <span className="arch-doc-chip approved" role="status">Approved · read only</span> : null}
          {chip ? <span className={'arch-doc-chip ' + chip.cls}>{chip.label}</span> : null}
          {projectId && canViewArchitecture && (!embedded || !archOpen) ? (
            embedded ? <WorkspacePaneToggle
              open={false} label="architecture"
              onToggle={() => setArchOpen(true)} controls="estimate-architecture-reference"
            /> : <button
              className={'arch-btn arch-btn-outline arch-btn-sm arch-doc-arch-toggle' + (archOpen ? ' active' : '')}
              onClick={() => setArchOpen((value) => !value)}
              aria-pressed={archOpen}
              title="Show the project architecture next to the document"
            >◧ {archOpen ? 'Hide architecture' : 'Show architecture'}</button>
          ) : null}
          <button
            className={
              'arch-btn arch-btn-outline arch-btn-sm arch-doc-chat-toggle' +
              (embedded ? ' arch-workspace-doc-icon arch-workspace-doc-ai-toggle' : '') +
              (chatOpen ? ' active' : '')
            }
            onClick={() => setChatOpen((o) => !o)}
            disabled={loading || !!loadError}
            aria-pressed={chatOpen}
            hidden={!rights.edit}
            aria-label="AI chat"
            title={immutable ? 'Ask about this approved estimate (Cmd/Ctrl+J)' : 'Toggle the AI editor (Cmd/Ctrl+J)'}
          >
            {embedded ? <Sparkles size={16} aria-hidden="true" /> : '✦ AI chat'}
          </button>
          {!readOnly && !embedded ? <button
            className={'arch-btn arch-btn-outline arch-btn-sm arch-workspace-doc-save' + (dirty ? ' dirty' : '')}
            onClick={save}
            disabled={!dirty || saving || loading || aiEditing}
          >
            {saving ? <span className="arch-spinner" /> : embedded ? <Save size={15} aria-hidden="true" /> : null}
            <span>{saving ? 'Saving…' : 'Save draft'}</span>
          </button> : null}
          <button
            className={'arch-btn arch-btn-outline arch-btn-sm' + (embedded ? ' arch-workspace-doc-icon' : '')}
            onClick={download}
            disabled={downloading || loading || aiEditing}
            aria-label={downloading ? 'Preparing download' : 'Download .docx'}
            title="Download the saved estimate (.docx)"
          >
            {embedded ? (downloading ? <span className="arch-spinner" /> : <Download size={16} aria-hidden="true" />) : (downloading ? 'Preparing…' : 'Download .docx')}
          </button>
          {!embedded ? <button
            className="arch-btn arch-btn-ghost arch-btn-sm"
            aria-label="Close"
            onClick={requestClose}
            disabled={saving}
          >
            ✕
          </button> : null}
        </div>
      </div>
      )}

      {embedded && refreshPending ? (
        <div className="arch-est-workspace-refresh" role="status">
          <span>{dirty ? 'Your unsaved draft is preserved.' : 'Reload the saved document to see the latest version.'}</span>
          <button className="arch-btn arch-btn-outline arch-btn-sm" disabled={saving || aiEditing} onClick={() => {
            if (dirty && !window.confirm('Discard your unsaved estimate changes and reload the saved version?')) return;
            forceRefreshRef.current = true;
            setRefreshAttempt((value) => value + 1);
          }}>Reload saved version</button>
        </div>
      ) : null}
    </>
  );

  const documentCanvas = (
    <EstimateReadOnlyContext.Provider value={readOnly || aiEditing}>
      <div className="arch-doc-canvas">
        {aiEditing ? (
          <div className="arch-doc-ai-overlay" role="status">
            <span className="arch-doc-ai-overlay-chip">✦ AI is editing the document…</span>
          </div>
        ) : null}
        {loading ? (
          <div className="arch-doc-loading">
            <span className="arch-spinner arch-doc-spinner-navy" />
            <span>Loading document…</span>
          </div>
        ) : loadError ? (
          <div className="arch-doc-loaderr">
            <p>{loadError}</p>
            <button className="arch-btn arch-btn-outline arch-btn-sm" onClick={() => onClose(savedAnyRef.current)}>
              ← Back to estimates
            </button>
          </div>
        ) : work ? (
          <div {...comments.surface} className={'arch-doc-page' + (aiEditing ? ' arch-doc-page-locked' : '') + (readOnly ? ' arch-doc-readonly' : '')}>
            {/* title */}
            <h1 className="arch-doc-h1">
              <IdentityField
                value={work.client}
                onChange={(v) => mutate((s) => (s.client = v))}
                placeholder="Client"
                ariaLabel="Client name"
                className="arch-doc-client"
              />
              <span className="arch-doc-title-suffix">{TITLE_SUFFIX}</span>
            </h1>

            {/* subtitle */}
            <AutoTextarea
              value={work.system}
              onChange={(v) => mutate((s) => (s.system = v))}
              placeholder="System / subtitle (optional)"
              ariaLabel="System name"
              className="arch-doc-h2"
            />

            {/* case sections */}
            {work.cases.map((c, ci) => {
              const sub = totals.perCase[ci] || { min: 0, max: 0, excluded: 0, invalid: 0 };
              return (
                <section {...commentBlockAttributes(comments.anchor(`cases.${ci}`, c.name || `Case ${ci + 1}`, c.name))} className="arch-doc-case" key={c._uid}>
                  <div className="arch-doc-case-head">
                    <AutoTextarea
                      value={c.name}
                      onChange={(v) => setCaseName(c._uid, v)}
                      placeholder={'Case ' + (ci + 1) + '. Section name'}
                      ariaLabel={'Case ' + (ci + 1) + ' name'}
                      className="arch-doc-case-name"
                    />
                    <div className="arch-doc-case-ctrl">
                      <CommentBlockButton anchor={comments.anchor(`cases.${ci}`, c.name || `Case ${ci + 1}`, c.name)} />
                      <button
                        className="arch-doc-rail-btn"
                        title="Move case up"
                        disabled={ci === 0}
                        onClick={() => moveCase(c._uid, -1)}
                      >
                        ↑
                      </button>
                      <button
                        className="arch-doc-rail-btn"
                        title="Move case down"
                        disabled={ci === work.cases.length - 1}
                        onClick={() => moveCase(c._uid, 1)}
                      >
                        ↓
                      </button>
                      <button
                        className="arch-doc-rail-btn del"
                        title="Delete case"
                        disabled={work.cases.length <= 1}
                        onClick={() => deleteCase(c._uid)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  {/* house table */}
                  <table className="arch-doc-table">
                    <colgroup>
                      <col style={{ width: '3.4%' }} />
                      <col style={{ width: '23.7%' }} />
                      <col style={{ width: '49.1%' }} />
                      <col style={{ width: '12.4%' }} />
                      <col style={{ width: '11.4%' }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Module / Task</th>
                        <th>Description</th>
                        <th>APIs / Integrations</th>
                        <th>Estimated Timeline</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.modules.map((m, i) => {
                        const marker = isMarker(m.days);
                        const invalid = m.days == null;
                        const rowKey = moduleKey(c.name, m.name);
                        const flashing = flashKeys.has(rowKey);
                        return (
                          <tr
                            {...commentBlockAttributes(comments.anchor(`cases.${ci}.modules.${i}`, m.name || `Module ${i + 1}`, `${c.name}: ${m.name}. ${m.description}`))}
                            key={m._uid}
                            ref={(el) => {
                              if (el) rowRefs.current.set(rowKey, el);
                            }}
                            className={
                              (marker ? 'arch-doc-tbd-row' : '') +
                              (flashing ? ' arch-doc-row-flash' : '')
                            }
                          >
                            <td className="arch-doc-num">{i + 1}</td>
                            <td data-label="Module / Task">
                              <div className="arch-comment-module-heading">
                              <AutoTextarea
                                value={m.name}
                                onChange={(v) => setModule(c._uid, m._uid, { name: v })}
                                placeholder="Module / task"
                                ariaLabel={'Module ' + (i + 1) + ' name'}
                                className="arch-doc-name"
                              />
                              <CommentBlockButton anchor={comments.anchor(`cases.${ci}.modules.${i}`, m.name || `Module ${i + 1}`, `${c.name}: ${m.name}. ${m.description}`)} />
                              </div>
                            </td>
                            <td data-label="Description">
                              <AutoTextarea
                                value={m.description}
                                onChange={(v) => setModule(c._uid, m._uid, { description: v })}
                                placeholder="Description"
                                ariaLabel={'Module ' + (i + 1) + ' description'}
                              />
                            </td>
                            <td data-label="APIs / Integrations">
                              <ApiField
                                value={m._apisRaw}
                                onChange={(v) => setModule(c._uid, m._uid, { _apisRaw: v })}
                                placeholder="—"
                                ariaLabel={'Module ' + (i + 1) + ' APIs'}
                                className="arch-doc-apis"
                              />
                            </td>
                            <td className="arch-doc-tl-cell" data-label="Estimated timeline · days">
                              <div className="arch-doc-tl-inner">
                                <input
                                  type="text"
                                  className={
                                    'arch-doc-field arch-doc-days-text' + (invalid ? ' invalid' : '')
                                  }
                                  value={m._daysRaw}
                                  placeholder="2 / 1–2 / TBD / –"
                                  title="Enter 0.5–60 days in 0.5-day steps, a range (1–2), TBD, or – (not applicable)"
                                  aria-label={'Module ' + (i + 1) + ' timeline'}
                                  aria-invalid={invalid || undefined}
                                  readOnly={readOnly || aiEditing}
                                  onChange={(e) => setDays(c._uid, m._uid, e.target.value)}
                                />
                              </div>
                              {marker ? (
                                <AutoTextarea
                                  value={m.tbd_reason}
                                  onChange={(v) => setModule(c._uid, m._uid, { tbd_reason: v })}
                                  placeholder={m.days === 'TBD' ? 'why TBD…' : 'why not applicable…'}
                                  ariaLabel={'Module ' + (i + 1) + ' reason'}
                                  className="arch-doc-reason"
                                />
                              ) : null}

                              {/* hover control rail (right margin) */}
                              <div className="arch-doc-rail">
                                <button
                                  className="arch-doc-rail-btn"
                                  title="Move up"
                                  disabled={i === 0}
                                  onClick={() => moveModule(c._uid, m._uid, -1)}
                                >
                                  ↑
                                </button>
                                <button
                                  className="arch-doc-rail-btn"
                                  title="Move down"
                                  disabled={i === c.modules.length - 1}
                                  onClick={() => moveModule(c._uid, m._uid, 1)}
                                >
                                  ↓
                                </button>
                                <button
                                  className="arch-doc-rail-btn del"
                                  title="Delete row"
                                  onClick={() => deleteModule(c._uid, m._uid)}
                                >
                                  ✕
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="arch-doc-addrow">
                        <td colSpan={5}>
                          <button className="arch-doc-add" onClick={() => addModule(c._uid)}>
                            + Add module
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* per-case subtotal */}
                  <div className="arch-doc-subtotal">
                    Subtotal: {daysLabel(sub.min, sub.max)}
                    {sub.excluded > 0 ? (
                      <span className="arch-doc-sub-note">
                        {' '}
                        · {sub.excluded} undefined
                      </span>
                    ) : null}
                  </div>
                </section>
              );
            })}

            {/* add case */}
            {work.cases.length < MAX_CASES ? (
              <button className="arch-doc-add ghost arch-doc-addcase" onClick={addCase}>
                + Add case
              </button>
            ) : (
              <div className="arch-doc-sub-note arch-doc-addcase">
                Maximum of {MAX_CASES} cases reached.
              </div>
            )}

            {/* document total */}
            <div className={'arch-doc-total' + (flash ? ' flash' : '')}>
              {totalLabel(totals.min, totals.max)}
              {totals.excluded > 0 ? (
                <span className="arch-doc-sub-note">
                  {' '}
                  · excludes {totals.excluded} undefined module
                  {totals.excluded === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>

            {/* Notes */}
            <div className="arch-doc-section">
              <div className="arch-doc-total-head">Notes</div>
              {work.notes.length ? (
                work.notes.map((n, i) => (
                  <div {...commentBlockAttributes(comments.anchor(`notes.${i}`, `Note ${i + 1}`, n))} className="arch-doc-q-row" key={'note' + i}>
                    <CommentBlockButton anchor={comments.anchor(`notes.${i}`, `Note ${i + 1}`, n)} />
                    <span className="arch-doc-q-dash">–</span>
                    <AutoTextarea
                      value={n}
                      onChange={(v) => setNote(i, v)}
                      placeholder="short, only what affects the timeline"
                      ariaLabel={'Note ' + (i + 1)}
                    />
                    <button className="arch-doc-x" title="Remove note" onClick={() => delNote(i)}>
                      ✕
                    </button>
                  </div>
                ))
              ) : (
                <div className="arch-doc-sub-note">
                  No notes yet — add anything that affects the timeline (assumptions,
                  dependencies, exclusions).
                </div>
              )}
              <button className="arch-doc-add subtle" onClick={addNote}>
                + add note
              </button>
            </div>

            {/* Summary Timeline */}
            <div className="arch-doc-section">
              <div className="arch-doc-total-head">Summary Timeline</div>
              <table className="arch-doc-table arch-doc-summary-table">
                <colgroup>
                  <col style={{ width: '49.1%' }} />
                  <col style={{ width: '33.8%' }} />
                  <col style={{ width: '17.1%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Scope / Deliverables</th>
                    <th>Timeline / Team</th>
                    <th>Team</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td data-label="Scope / Deliverables">
                      <AutoTextarea
                        value={work.summary.deliverables}
                        onChange={setDeliverables}
                        placeholder="Condensed scope / deliverables…"
                        ariaLabel="Summary deliverables"
                      />
                    </td>
                    <td data-label="Timeline / Team">
                      <div className="arch-doc-summary-timeline" aria-label="Timeline and team">
                        {summaryTimeline}
                      </div>
                      <IdentityField
                        value={work.summary.team}
                        onChange={setTeam}
                        placeholder={DEFAULT_TEAM}
                        ariaLabel="Team"
                        className="arch-doc-team-input"
                      />
                    </td>
                    <td data-label="Team">
                      <span className="arch-doc-expense">Expense:</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </EstimateReadOnlyContext.Provider>
  );

  return (
    <div
      className={embedded ? 'arch-est-workspace arch-est-workspace-document' : 'arch-doc-overlay'}
      role={embedded ? 'region' : 'dialog'}
      aria-modal={embedded ? undefined : true}
      aria-label={immutable ? 'View approved estimate' : readOnly ? 'View estimate document' : 'Edit estimate document'}
    >
      {documentControls}

      {/* body: architecture pane (optional) + page canvas + AI chat panel */}
      <div
        className={
          'arch-doc-body' +
          (archOpen && (!archMobile || embedded) ? ' arch-doc-body-split' : '') +
          (chatOpen ? ' arch-doc-body-chat' : '') +
          (archDragging ? ' arch-doc-body-resizing' : '')
        }
        ref={bodyRef}
      >
      {archOpen ? (
        <>
          <aside
            className={'arch-doc-arch' + (archMobile && !embedded ? ' sheet' : '')}
            style={archMobile ? undefined : { width: archWidth + 'px' }}
            aria-label="Project architecture"
            id={embedded ? 'estimate-architecture-reference' : undefined}
          >
            <div className={'arch-doc-arch-head' + (embedded ? ' arch-workspace-pane-head' : '')}>
              <span className="arch-doc-arch-title">Architecture</span>
              <div className="arch-doc-arch-tools">
                <button
                  className="arch-doc-rail-btn"
                  title="Zoom out"
                  aria-label="Zoom out"
                  onClick={() => archZoom(1 / 1.25)}
                  disabled={!archSvg}
                >
                  −
                </button>
                <span className="arch-doc-arch-zoom">{Math.round(archScale * 100)}%</span>
                <button
                  className="arch-doc-rail-btn"
                  title="Zoom in"
                  aria-label="Zoom in"
                  onClick={() => archZoom(1.25)}
                  disabled={!archSvg}
                >
                  +
                </button>
                <button
                  className="arch-doc-arch-fit"
                  title="Fit the poster width"
                  onClick={archFit}
                  disabled={!archSvg}
                >
                  Fit
                </button>
                {embedded ? <WorkspacePaneToggle
                  open label="architecture" shortLabel
                  onToggle={() => setArchOpen(false)} controls="estimate-architecture-reference"
                /> : <button
                  className="arch-doc-arch-close"
                  title="Close the architecture pane"
                  aria-label="Close the architecture pane"
                  onClick={() => setArchOpen(false)}
                >
                  ✕
                </button>}
              </div>
            </div>
            <div className="arch-doc-arch-scroll" ref={archScrollRef}>
              {archLoading ? (
                <div className="arch-doc-arch-msg">
                  <span className="arch-spinner arch-doc-spinner-navy" />
                  <span>Loading architecture…</span>
                </div>
              ) : archError ? (
                <div className="arch-doc-arch-msg">
                  <p>{archError}</p>
                  {/* Retry only makes sense for a failed fetch — an empty
                      poster is a fact about the project, not an error. */}
                  {archSvg === '' ? null : (
                    <button
                      className="arch-btn arch-btn-outline arch-btn-sm"
                      onClick={() => setArchReload((n) => n + 1)}
                    >
                      Try again
                    </button>
                  )}
                </div>
              ) : null}
              <div className="arch-doc-arch-stage" ref={archStageRef} />
            </div>
          </aside>
          {!archMobile ? (
            <div
              className="arch-doc-arch-split"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize the architecture pane"
              tabIndex={0}
              onPointerDown={onSplitDown}
              onKeyDown={onSplitKey}
            />
          ) : null}
        </>
      ) : null}
      {embedded ? (
        <div className="arch-est-document-column">
          {documentCanvas}
        </div>
      ) : documentCanvas}

      {/* AI chat side panel / mobile bottom sheet */}
      {rights.edit && <EstimateChat
        estimateId={estimateId}
        open={chatOpen && active}
        onOpen={embedded ? () => { if (!loading && !loadError) setChatOpen(true); } : undefined}
        launcherTarget={embedded ? aiLauncherHost : undefined}
        openDisabled={loading || !!loadError}
        onClose={() => setChatOpen(false)}
        saving={saving}
        askOnly={immutable}
        onSend={aiEdit}
      />}
      </div>
      {embedded ? <div className="stage-ai-launcher-host" ref={setAiLauncherHost} /> : null}
    </div>
  );
}
